import { AuthenticationService } from '../services/AuthenticationService.ts';
import { useWebSocketStore } from '@/stores/WebSocketStore.ts';
import { wsAuthLogIn } from '@/ws/requests/WsAuthLogIn.ts';
import { useSessionStore } from '@/stores/SessionStore.ts';
import { WS_REQUEST, WS_RESPONSE } from '@/types/ws/WebSocketEvents.ts';
import type { WebSocketStreamData } from '@/types/ws/WebSocketStreamData.ts';
import { WebSocketStreamDataType } from '@/types/ws/WebSocketStreamDataType.ts';
import { useClientStore } from '@/stores/ClientStore.ts';
import { eventEmitter } from '@/lib/eventEmitter.ts';
import type { WebSocketMessageData } from '@/types/ws/WebSocketMessageData.ts';
import { connectedClientsMessageHandler } from '@/ws/handlers/ConnectedClientsMessageHandler.ts';
import { syncClientSyncMessageHandler } from '@/ws/handlers/SyncClientSyncMessageHandler.ts';
import { authLogInMessageHandler } from '@/ws/handlers/AuthLogInMessageHandler.ts';
import { logger } from '@/lib/logger.ts';

const MAX_SEND_RETRY_ATTEMPTS = 5;

class WebSocketService {
    static #instance: WebSocketService;

    private socket: WebSocket | undefined = undefined;

    messageHandlers: { [key: string]: (messageData: WebSocketMessageData) => void } = {
        [WS_RESPONSE.auth_logIn]: authLogInMessageHandler,
        [WS_RESPONSE.connectedClients]: connectedClientsMessageHandler,
        [WS_RESPONSE.sync_clientSync]: syncClientSyncMessageHandler,
    };

    public static get instance(): WebSocketService {
        if (!WebSocketService.#instance) {
            WebSocketService.#instance = new WebSocketService();
        }

        return WebSocketService.#instance;
    }

    public connect(): void {
        if (this.socket && this.socket.readyState !== WebSocket.CLOSED) {
            return;
        }

        const webSocketStore = useWebSocketStore();
        webSocketStore.shouldReconnect = true;
        webSocketStore.showWebSocketStatus = true;

        this.socket = new WebSocket(import.meta.env.VITE_WS_SERVER_URL);

        this.socket.addEventListener('open', () => {
            webSocketStore.isConnected = true;

            const isLoggedIn = AuthenticationService.instance.isLoggedIn();
            const accessToken = AuthenticationService.instance.getAccessToken();

            if (isLoggedIn && accessToken) {
                const sessionStore = useSessionStore();
                wsAuthLogIn(accessToken, !sessionStore.isInsideWebView);
            }
        });

        this.socket.addEventListener('message', (message) => {
            let messageData;
            try {
                messageData = JSON.parse(message.data);
            } catch (e) {
                logger.error('WebSocket parsing error:', e);

                return;
            }

            if (messageData.event === undefined) {
                return;
            }

            const event = messageData.event;
            if (event === WS_RESPONSE.rateLimited) {
                throw new Error('Rate limited by WS server');
            }

            const data = messageData.data;

            // logger.debug(data);

            if (data?.streamId !== undefined) {
                this.handleStreamMessage(data);
            } else {
                if (event !== undefined && this.messageHandlers[event] !== undefined) {
                    this.messageHandlers[event](data);
                }
            }
        });

        this.socket.addEventListener('close', () => {
            webSocketStore.isConnected = false;
        });
    }

    public verifyConnection(): void {
        if (!this.socket || this.socket.readyState === WebSocket.CLOSED) {
            this.connect();
        } else {
            const webSocketStore = useWebSocketStore();
            webSocketStore.isConnected = true;
        }
    }

    public disconnect(): void {
        if (this.socket) {
            this.socket.close();
        }

        const webSocketStore = useWebSocketStore();
        webSocketStore.shouldReconnect = false;

        setTimeout(() => {
            webSocketStore.showWebSocketStatus = false;
        }, 2_500);
    }

    /* eslint-disable @typescript-eslint/no-explicit-any */
    public send(requestEvent: WS_REQUEST, data: any, retryAttempt = 0): void {
        if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
            if (retryAttempt > MAX_SEND_RETRY_ATTEMPTS) {
                logger.warn(
                    `Failed to send "${requestEvent}" message after ${MAX_SEND_RETRY_ATTEMPTS} retries, terminating send.`,
                );

                return;
            }

            logger.warn(
                `Failed to send "${requestEvent}" message, socket is still undefined or not opened yet, retrying in 100ms (attempt ${retryAttempt + 1}).`,
            );

            setTimeout(() => {
                this.send(requestEvent, data, retryAttempt + 1);
            }, 100);

            return;
        }

        try {
            this.socket.send(
                JSON.stringify({
                    event: requestEvent,
                    data,
                }),
            );
        } catch (error) {
            logger.error('Failed to send WebSocket message:', error);
        }
    }

    private handleStreamMessage(messageData: WebSocketStreamData): void {
        const clientStore = useClientStore();

        const type = messageData.type;
        const streamId = messageData.streamId;

        // Add to output if it is a data type, we don't add start and finish to the output.
        if (type === WebSocketStreamDataType.Start) {
            // Prepare output
            clientStore.streams[streamId] = '';

            // Remove from "waiting" state
            clientStore.streamsWaiting = clientStore.streamsWaiting.filter(
                (id: string) => id !== streamId,
            );

            // Remove from "errored state
            clientStore.streamsErrored = clientStore.streamsErrored.filter(
                (id: string) => id !== streamId,
            );

            // Add to "in progress" state (check if ended, because end can arrive before start)
            if (!clientStore.streamsEnded.includes(streamId)) {
                clientStore.streamsInProgress = [...clientStore.streamsInProgress, streamId];
            }
        }

        if (type === WebSocketStreamDataType.Content) {
            const content = messageData.content;
            clientStore.streams[streamId] += content;
            eventEmitter.emit('streamContent', {
                streamId: streamId,
                deltaContent: content ?? '',
            });
        }

        if (type === WebSocketStreamDataType.End) {
            // End may arrive too early
            setTimeout(() => {
                // Remove from "in progress" state
                clientStore.streamsInProgress = clientStore.streamsInProgress.filter(
                    (id: string) => id !== streamId,
                );

                // Add to "ended" state
                clientStore.streamsEnded = [...clientStore.streamsEnded, streamId];

                eventEmitter.emit('streamFinished', {
                    streamId: streamId,
                    fullContent: clientStore.streams[streamId],
                });
            }, 100);
        }

        if (type === WebSocketStreamDataType.Error) {
            clientStore.streamsWaiting = clientStore.streamsWaiting.filter((id) => id !== streamId);
            clientStore.streamsInProgress = clientStore.streamsInProgress.filter(
                (id) => id !== streamId,
            );
            if (!clientStore.streamsErrored.includes(streamId)) {
                clientStore.streamsErrored = [...clientStore.streamsErrored, streamId];
            }
            clientStore.streamErrors[streamId] =
                messageData.content ?? 'Prompt failed. Please try again.';
        }
    }
}

export { WebSocketService };
